import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../../../firebaseConfig';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';


// Async thunks for notification operations
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ userId, limitCount = 20, lastDoc = null, filter = 'all' }, { rejectWithValue }) => {
    try {
      let q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      // Add filter conditions
      if (filter === 'unread') {
        q = query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          where('read', '==', false),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else if (filter === 'social') {
        q = query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          where('category', '==', 'social'),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else if (filter === 'projects') {
        q = query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          where('category', '==', 'project'),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      } else if (filter === 'solutions') {
        q = query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          where('category', '==', 'solution'),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
      }

      // Pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs
        .filter(doc => doc.data().deleted !== true)
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));

      return {
        notifications,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: notifications.length === limitCount
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationIds, { rejectWithValue }) => {
    try {
      const batch = writeBatch(db);

      notificationIds.forEach(id => {
        const notifRef = doc(db, 'notifications', id);
        batch.update(notifRef, {
          read: true,
          readAt: serverTimestamp()
        });
      });

      await batch.commit();
      return notificationIds;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      showErrorToast('Failed to mark notifications as read');
      return rejectWithValue(error.message);
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (userId, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);

      // FIX HIGH-2: Firestore writeBatch is hard-limited to 500 operations.
      // For users with >500 unread notifications one batch.commit() would throw.
      // Chunk the docs into slices of 450 (safe margin) and commit each separately.
      const CHUNK_SIZE = 450;
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += CHUNK_SIZE) {
        const chunk = docs.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(docSnap => {
          batch.update(docSnap.ref, {
            read: true,
            readAt: serverTimestamp()
          });
        });
        await batch.commit();
      }

      // Only show success toast when there was something to mark
      if (docs.length > 0) {
        showSuccessToast('All notifications marked as read');
      }

      return docs.map(docSnap => docSnap.id);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showErrorToast('Failed to mark all notifications as read');
      return rejectWithValue(error.message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      const currentUid = auth?.currentUser?.uid || null;
      const purgeAfter = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      await updateDoc(notifRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUid,
        purgeAfter,
        updatedAt: serverTimestamp()
      });
      return notificationId;
    } catch (error) {
      console.error('Error deleting notification:', error);
      showErrorToast('Failed to delete notification');
      return rejectWithValue(error.message);
    }
  }
);

export const undoDeleteNotification = createAsyncThunk(
  'notifications/undoDeleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, {
        deleted: false,
        deletedAt: null,
        deletedBy: null,
        purgeAfter: null,
        updatedAt: serverTimestamp()
      });
      showSuccessToast('Notification restored');
      return notificationId;
    } catch (error) {
      console.error('Error restoring notification:', error);
      showErrorToast('Failed to restore notification');
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,

  // Pagination
  hasMore: true,
  lastVisible: null,

  // Filters
  currentFilter: 'all', // 'all' | 'unread' | 'social' | 'projects'

  // Real-time listener
  isListenerActive: false,

  // Undo support
  lastDeletedNotification: null,

  // Settings (excluding email for now)
  preferences: {
    inApp: true,
    desktop: true,
    sound: true,
    aggregateDelay: 300000, // 5 minutes in milliseconds
    types: {
      likes: true,
      comments: true,
      replies: true,
      follows: true,
      mentions: true,
      projects: true,
      solutions: true,
      messages: true
    }
  }
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Real-time notification updates
    addNotification: (state, { payload }) => {
      // Check if notification already exists (prevent duplicates)
      const existingIndex = state.notifications.findIndex(n => n.id === payload.id);

      if (existingIndex === -1) {
        // Add new notification to the beginning
        state.notifications.unshift({
          ...payload,
          createdAt: payload.createdAt?.toDate ? payload.createdAt.toDate() : new Date(payload.createdAt)
        });

        // Update unread count
        if (!payload.read) {
          state.unreadCount += 1;
        }

        // Keep only latest 100 notifications in memory for performance
        if (state.notifications.length > 100) {
          state.notifications = state.notifications.slice(0, 100);
        }
      }
    },

    updateNotification: (state, { payload }) => {
      const index = state.notifications.findIndex(n => n.id === payload.id);
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        state.notifications[index] = { ...state.notifications[index], ...payload };

        // Update unread count if read status changed
        if (wasUnread && payload.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },

    removeNotification: (state, { payload }) => {
      const index = state.notifications.findIndex(n => n.id === payload);
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        state.notifications.splice(index, 1);

        // Update unread count
        if (wasUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },

    setUnreadCount: (state, { payload }) => {
      state.unreadCount = payload;
    },

    setFilter: (state, { payload }) => {
      state.currentFilter = payload;
      // Reset pagination when filter changes
      state.hasMore = true;
      state.lastVisible = null;
    },

    updatePreferences: (state, { payload }) => {
      state.preferences = { ...state.preferences, ...payload };
    },

    setListenerActive: (state, { payload }) => {
      state.isListenerActive = payload;
    },

    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.hasMore = true;
      state.lastVisible = null;
    },

    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, { payload, meta }) => {
      state.loading = false;

      // If this is a fresh fetch (no lastDoc), replace notifications
      if (!meta.arg.lastDoc) {
        state.notifications = payload.notifications;
      } else {
        // Phase 7 Fix: Add duplicate append guard for pagination
        const existingIds = new Set(state.notifications.map(n => n.id));
        const newNotifications = payload.notifications.filter(n => !existingIds.has(n.id));
        state.notifications.push(...newNotifications);
      }

      // Phase 7 Fix: Add memory cap to prevent unbounded growth
      const MAX_NOTIFICATIONS = 100;
      if (state.notifications.length > MAX_NOTIFICATIONS) {
        state.notifications = state.notifications.slice(0, MAX_NOTIFICATIONS);
      }

      state.hasMore = payload.hasMore;
      state.lastVisible = payload.lastVisible;

      // Calculate unread count from server response for accuracy
      state.unreadCount = payload.unreadCount ?? state.notifications.filter(n => !n.read).length;
    });
    builder.addCase(fetchNotifications.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    // Mark as read
    builder.addCase(markAsRead.fulfilled, (state, { payload }) => {
      payload.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification && !notification.read) {
          notification.read = true;
          notification.readAt = new Date();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
    });

    // Mark all as read
    builder.addCase(markAllAsRead.fulfilled, (state, { payload }) => {
      payload.forEach(id => {
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          notification.read = true;
          notification.readAt = new Date();
        }
      });
      state.unreadCount = 0;
    });

    // Delete notification
    builder.addCase(deleteNotification.fulfilled, (state, { payload }) => {
      const index = state.notifications.findIndex(n => n.id === payload);
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        // Store deleted notification for undo
        state.lastDeletedNotification = { ...state.notifications[index] };
        state.notifications.splice(index, 1);

        if (wasUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    });

    // Undo delete notification - restore from lastDeletedNotification
    builder.addCase(undoDeleteNotification.fulfilled, (state, { payload }) => {
      if (state.lastDeletedNotification && state.lastDeletedNotification.id === payload) {
        // Restore the notification
        state.notifications.unshift(state.lastDeletedNotification);
        // Re-sort by createdAt
        state.notifications.sort((a, b) => {
          const aTime = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const bTime = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return bTime - aTime;
        });
        // Restore unread count if it was unread
        if (!state.lastDeletedNotification.read) {
          state.unreadCount++;
        }
        state.lastDeletedNotification = null;
      }
    });
  }
});

// Export actions
export const {
  addNotification,
  updateNotification,
  removeNotification,
  setUnreadCount,
  setFilter,
  updatePreferences,
  setListenerActive,
  clearNotifications,
  clearError
} = notificationsSlice.actions;

// Selectors
export const selectNotifications = (state) => state.notifications.notifications;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectNotificationsError = (state) => state.notifications.error;
export const selectCurrentFilter = (state) => state.notifications.currentFilter;
export const selectHasMore = (state) => state.notifications.hasMore;
export const selectNotificationPreferences = (state) => state.notifications.preferences;
export const selectIsListenerActive = (state) => state.notifications.isListenerActive;

// Filtered notifications selector
export const selectFilteredNotifications = (state) => {
  const { notifications, currentFilter } = state.notifications;

  switch (currentFilter) {
    case 'unread':
      return notifications.filter(n => !n.read);
    case 'social':
      return notifications.filter(n => n.category === 'social');
    case 'projects':
      return notifications.filter(n => n.category === 'project');
    case 'solutions':
      return notifications.filter(n => n.category === 'solution');
    default:
      return notifications;
  }
};

export default notificationsSlice.reducer;
